import { Mail } from "lucide-react";

import { Button, buttonVariants } from "@krea/shared/design-system/Button";
import { AppModal } from "@krea/shared/design-system/modal";
import { Separator } from "@krea/shared/design-system/Separator";

import { OfferAttractivenessRecommendationStatusEnum } from "@/features/common/enums";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";

import { OfferAttractivenessCard } from "./offer-attractiveness-card";
import { getOfferAttractivenessContactEmail } from "./utils";

const ENQUIRY_SUBJECT = "Offer Attractiveness Access Request";
const ENQUIRY_BODY =
  "Hello! I would like to get access to Offer Attractiveness.";

const TEASER_SCORE = 4;
const TEASER_RECOMMENDATION = {
  status: OfferAttractivenessRecommendationStatusEnum.INCREASE_AMOUNT,
  statusText: "Increase the amount",
} as const;

interface EnquireAboutAttractivenessModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const EnquireAboutAttractivenessModal = ({
  open,
  setOpen,
}: EnquireAboutAttractivenessModalProps) => {
  // Cached by React Query — this is already fetched wherever the locked card renders.
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const email = getOfferAttractivenessContactEmail(lenderCompany?.countryCode);

  return (
    <AppModal
      open={open}
      setOpen={setOpen}
      lockOutsideInteraction={false}
      hideCloseButton={false}
      title="Enquire About Offer Attractiveness"
    >
      <div className="max-h-[70vh] overflow-y-auto">
        <div className="space-y-6">
          <p className="text-base">Offer attractiveness preview</p>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <OfferAttractivenessCard
              score={TEASER_SCORE}
              recommendations={[TEASER_RECOMMENDATION]}
              bidType={undefined}
              recommendationsClickable={false}
            />
          </div>
          <div>
            <p className="text-base-strong">
              Get access to Offer Attractiveness
            </p>
            <ul className="mb-0 list-disc space-y-1.5 pl-5 text-sm text-black">
              <li>See a score for how attractive your offer is to customers</li>
              <li>Understand how likely your offer is to convert</li>
              <li>Get tailored recommendations to improve your offer</li>
            </ul>
          </div>
          <Separator className="my-2" />
          <h3 className="text-section">Contact us to get access</h3>
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-150">
                <Mail className="h-4 w-4 text-primary" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <p className="mb-1 text-label">E-mail</p>
                <p className="mb-2 break-all text-base-strong">{email}</p>
              </div>
              {/* Styled as a button rather than wrapped in one — see the keep-feature
                  modal: an <a> inside <Button> nests two interactive elements. */}
              <a
                href={`mailto:${email}?subject=${encodeURIComponent(ENQUIRY_SUBJECT)}&body=${encodeURIComponent(ENQUIRY_BODY)}`}
                className={buttonVariants()}
              >
                <Mail className="h-4 w-4" />
                <span>Send Email</span>
              </a>
            </div>
          </div>
          <Separator className="my-2" />

          <div className="flex justify-end pt-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </AppModal>
  );
};

export { EnquireAboutAttractivenessModal };
